import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { EmailBouncesTable } from '../-components/EmailBouncesTable';
import { emailBouncesQueryOptions } from '../-hooks/useEmailBounces';
import { useEmailBouncesPage } from '../-hooks/useEmailBouncesPage';

const emailBouncesSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  // The endpoint caps page size at 500 (`defaultPaginationConfig.maxLimit`);
  // anything above it fails the schema, so it is clamped here rather than sent.
  limit: fallback(z.number().int().min(10).max(500), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute(
  '/management/email-bounce-tracking/(pages)/',
)({
  validateSearch: emailBouncesSearch,
  // Read off the URL rather than fixed to the first page, so a deep link with a
  // filter prefetches the rows it will actually show. `emailBouncesQueryOptions`
  // derives the key from the converted body, which is what makes this agree with
  // the component's own query.
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  // Swallowed: a rejected loader hands the route to the router's error
  // boundary, and the page already reports a failed load as a banner above the
  // table.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(emailBouncesQueryOptions(deps))
      .catch(() => undefined),
  component: EmailBounceTrackingPage,
});

function EmailBounceTrackingPage() {
  const page = useEmailBouncesPage();

  return (
    <PageContainer>
      <EmailBouncesTable page={page} />
    </PageContainer>
  );
}
